import {
  FileUpload,
  LargeFileUploadTask,
  LargeFileUploadTaskOptions,
  UploadResult
} from '@microsoft/microsoft-graph-client';
import { DriveItem, ThumbnailSet } from '@microsoft/microsoft-graph-types';
import { useVariable } from '@softwareimaging/backstage';
import { Variables } from '../../backstage/config';
import { GraphResponse, useGraph } from '../GraphProvider';
import { useQuery } from '../QueryProvider';
import { useMutation } from 'react-query';

export const useGetChildren = (id: string) => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('driveId');

  return useQuery<GraphResponse<DriveItem[]>>(['folders', id], async () => {
    return await client.api(`/drives/${driveId}/items/${id}/children`).expand('listItem').get();
  });
};

export const useGetChildrenRecursive = (folderId: string) => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('driveId');

  return useQuery<GraphResponse<DriveItem[]>>(['recursiveChildren', folderId], async () => {
    return await client.api(`/drives/${driveId}/items/${folderId}/search(q='')`).get();
  });
};

export const useSearch = (searchTerm: string, folderId: string) => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('driveId');

  return useQuery<GraphResponse<DriveItem[]>>(['searchResults', searchTerm, folderId], async () => {
    return await client.api(`/drives/${driveId}/items/${folderId}/search(q='${searchTerm}')`).get();
  });
};

export const useGetRecentlyModified = (id: string) => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('driveId');

  return useQuery<GraphResponse<DriveItem[]>>(['recent', id], async () => {
    return await client.api(`/drives/${driveId}/items/${id}/search(q='')?orderby=name%20desc&top=20`).get();
  });
};

export const useGetThumbnail = (id?: string) => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('driveId');

  return useQuery<GraphResponse<ThumbnailSet[]>>(
    ['thumbnails', id || ''],
    async () => {
      return await client.api(`/drives/${driveId}/items/${id}/thumbnails`).get();
    },
    { enabled: !!id }
  );
};

export const useGetItem = (id: string) => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('driveId');

  return useQuery<DriveItem>(['item', id], async () => {
    return await client.api(`/drives/${driveId}/items/${id}`).get();
  });
};

export const useUploadJSON = () => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('driveId');
  return async (parentId: string, name: string, content: Record<string, any>) => {
    const response: DriveItem = await client
      .api(`/drives/${driveId}/items/${parentId}/children/${name}/content`)
      .headers({ 'Content-Type': 'application/json' })
      .put(JSON.stringify(content));
    return response;
  };
};

export const useUploadFile = () => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('driveId');

  return async (parentId: string, fileName: string, content: File) => {
    const payload = {
      item: {
        '@microsoft.graph.conflictBehavior': 'rename',
        name: fileName
      }
    };

    const uploadSession = await LargeFileUploadTask.createUploadSession(
      client,
      `/drives/${driveId}/items/${parentId}:/${fileName}:/createUploadSession`,
      payload
    );

    const options: LargeFileUploadTaskOptions = {
      rangeSize: content.size
    };

    const fileObject = new FileUpload(content, fileName, content.size);

    const uploadTask = new LargeFileUploadTask(client, fileObject, uploadSession, options);
    const uploadResult: UploadResult = await uploadTask.upload();

    return uploadResult;
  };
};

export const useReplaceFile = () => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('driveId');

  return async (originalFileId: string, originalFileName: string, newContent: File) => {
    const payload = {
      item: {
        '@microsoft.graph.conflictBehavior': 'replace',
        name: originalFileName
      }
    };

    const uploadSession = await LargeFileUploadTask.createUploadSession(
      client,
      `/drives/${driveId}/items/${originalFileId}/createUploadSession`,
      payload
    );

    const options: LargeFileUploadTaskOptions = {
      rangeSize: newContent.size
    };

    const fileObject = new FileUpload(newContent, originalFileName, newContent.size);

    const uploadTask = new LargeFileUploadTask(client, fileObject, uploadSession, options);
    const uploadResult: UploadResult = await uploadTask.upload();

    return uploadResult;
  };
};

export const useDeleteItem = () => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('driveId');

  return async (id: string) => {
    return await client.api(`/drives/${driveId}/items/${id}`).delete();
  };
};

export const useFetchItem = (id: string) => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('driveId');

  return async () => {
    const result = await client.api(`/drives/${driveId}/items/${id}`).get();

    return result as DriveItem;
  };
};

export enum ThumbnailSize {
  LARGE,
  MED,
  SMALL
}

export const useThumbnailUrl = (id?: string, size: ThumbnailSize = ThumbnailSize.LARGE) => {
  const [thumbnailsData] = useGetThumbnail(id);

  if (!thumbnailsData || !thumbnailsData.value || !thumbnailsData.value[0]) {
    return undefined;
  }

  const thumbnails = thumbnailsData.value[0];

  let url: string | undefined = undefined;
  switch (size) {
    case ThumbnailSize.LARGE:
      url = thumbnails.large?.url || undefined;
      if (url) {
        break;
      }
    case ThumbnailSize.MED:
      url = thumbnails.medium?.url || undefined;
      if (url) {
        break;
      }
    case ThumbnailSize.SMALL:
      url = thumbnails.small?.url || undefined;
  }

  return url;
};

export const useCreateFolder = () => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('driveId');

  return useMutation(
    ['createFolder'],
    async (variables: { parentId: string; newFolderName: string }) => {
      const payload = {
        '@microsoft.graph.conflictBehavior': 'rename',
        name: variables.newFolderName,
        folder: {}
      };
      const result = await client.api(`/drives/${driveId}/items/${variables.parentId}/children`).post(payload);

      return result as DriveItem;
    },
    {}
  );
};
